import React, { useContext, useEffect, useState } from 'react';
import { Widget, Button, Table } from '@digital/ui';
import { Select, Spin, Icon, Input, Modal, Pagination, Alert } from 'antd';
import { CrudContext } from '@digital/ui';
import {
  getNaoConformesTodos,
  getPendenciasByContrato,
  aprovarNotificacao,
  reprovarNotificacao,
  getUrlAssinaturaNotificacao,
  reativarNotificacoesPendencia,
  exportarNaoConformesContrato,
  NotificacaoPendenciaContrato,
  PendenciaDocumental,
} from '../../../api/pendenciaDocumental';

const selectFilter = (options: string[], onFilter: (value: any, record: any) => boolean) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
    <div style={{ padding: 8 }}>
      <Select
        showSearch
        placeholder="Selecionar"
        style={{ width: 180, marginBottom: 8, display: 'block' }}
        value={selectedKeys[0]}
        onChange={(value: any) => { setSelectedKeys(value !== undefined ? [value] : []); confirm(); }}
        allowClear>
        {options.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
      </Select>
      <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>Limpar</Button>
    </div>
  ),
  filterIcon: (filtered: boolean) => (
    <Icon
      type="search"
      className={filtered ? 'anticon-filter ant-table-filter-selected' : ''}
      style={{ minWidth: 'auto', border: 'none' }}
    />
  ),
  onFilter,
});

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  Pendente: { label: 'Pendente', color: '' },
  PendenteEnvioEmail: { label: 'Pendente de Envio', color: 'orange' },
  PendenteEnvioFornecedorAssinado: { label: 'Pendente de Envio (Assinado)', color: 'orange' },
  PendenteEnvioReprovacaoGestor: { label: 'Pendente de Envio (Reprovada)', color: 'orange' },
  ProcessandoEnvioEmail: { label: 'Processando Envio', color: 'blue' },
  ErroEnvioFornecedor: { label: 'Erro no Envio ao Fornecedor', color: 'red' },
  ErroEnvioGestor: { label: 'Erro no Envio ao Gestor', color: 'red' },
  ErroEnvioFornecedorAssinado: { label: 'Erro no Envio (Assinado)', color: 'red' },
  ErroEnvioReprovacaoGestor: { label: 'Erro no Envio (Reprovada)', color: 'red' },
  FalhaEnvioEmail: { label: 'Falha no Envio', color: 'red' },
  PendenteAprovacao: { label: 'Pendente de Aprovação', color: 'gold' },
  Aprovada: { label: 'Aprovada pelo Gestor', color: 'green' },
  AguardandoAssinatura: { label: 'Aguardando Assinatura', color: 'purple' },
  AssinaturaExpirada: { label: 'Assinatura Expirada', color: 'red' },
  AssinaturaRecusada: { label: 'Assinatura Recusada', color: 'red' },
  Pausada: { label: 'Notificação Pausada', color: 'orange' },
  NaoNotificado: { label: 'Não Notificado', color: '' },
  Reprovada: { label: 'Reprovada', color: 'red' },
  EnviadaFornecedor: { label: 'Enviada ao Fornecedor', color: 'cyan' },
  EnviadaDireta: { label: 'Enviada Diretamente', color: 'cyan' },
};

interface AprovacaoPanelProps {
  notificacao: NotificacaoPendenciaContrato;
  onAcao: () => void;
  podeAgir: boolean;
}

const AprovacaoPanel = ({ notificacao, onAcao, podeAgir }: AprovacaoPanelProps) => {
  const [actionLoading, setActionLoading] = useState(false);
  const [comentario, setComentario] = useState('');
  const [reprovarVisible, setReprovarVisible] = useState(false);
  const [aprovarVisible, setAprovarVisible] = useState(false);

  const aguardandoAssinatura = notificacao.statusNotificacao === 'AguardandoAssinatura';
  const assinaturaExpirada = notificacao.statusNotificacao === 'AssinaturaExpirada';
  const assinaturaRecusada = notificacao.statusNotificacao === 'AssinaturaRecusada';
  const reenvioAssinatura = assinaturaExpirada || assinaturaRecusada;

  const handleAprovar = async () => {
    setActionLoading(true);
    try {
      const resp = await aprovarNotificacao(notificacao.id);
      if (resp && resp.valid) {
        setAprovarVisible(false);
        if (resp.urlAssinatura) {
          window.location.href = resp.urlAssinatura;
        } else {
          onAcao();
        }
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssinar = async () => {
    setActionLoading(true);
    try {
      const resp: any = await getUrlAssinaturaNotificacao(notificacao.id);
      const data = resp && resp.data ? resp.data : resp;
      if (data && data.valid !== false && data.urlAssinatura) {
        window.location.href = data.urlAssinatura;
      } else if (resp && resp.urlAssinatura) {
        window.location.href = resp.urlAssinatura;
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleReprovar = async () => {
    if (comentario.trim().length < 10) return;
    setActionLoading(true);
    try {
      const resp = await reprovarNotificacao(notificacao.id, comentario);
      if (resp && resp.valid) {
        setReprovarVisible(false);
        setComentario('');
        onAcao();
      }
    } finally {
      setActionLoading(false);
    }
  };

  const tituloPainel = aguardandoAssinatura
    ? 'Aguardando Assinatura'
    : assinaturaExpirada
      ? 'Assinatura Expirada'
      : assinaturaRecusada
        ? 'Assinatura Recusada'
        : 'Pendente de Aprovação';

  const corIcone = reenvioAssinatura || aguardandoAssinatura ? (reenvioAssinatura ? '#ff4d4f' : '#722ed1') : '#faad14';

  return (
    <Widget style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#595959' }}>
          <Icon type="warning" style={{ color: corIcone, fontSize: 14 }} />
          <span>{tituloPainel}</span>
          <span style={{ color: '#8c8c8c' }}>
            Competência: {notificacao.competenciaMonitoramento}
          </span>
          {notificacao.numeroProtocolo && (
            <span style={{ color: '#8c8c8c' }}>Protocolo: {notificacao.numeroProtocolo}</span>
          )}
          {notificacao.temDocumentoCritico && (
            <span style={{ color: '#ff4d4f' }}>Contém documentos críticos</span>
          )}
          {!podeAgir && (
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>
              {aguardandoAssinatura ? 'Aguardando assinatura do gestor' : 'Aguardando ação do gestor'}
            </span>
          )}
        </span>
        {podeAgir && (
          <span style={{ display: 'flex', gap: 8 }}>
            {aguardandoAssinatura ? (
              <Button type="primary" loading={actionLoading} onClick={handleAssinar}>
                Assinar no DocuSign
              </Button>
            ) : (
              <>
                <Button type="primary" onClick={() => setAprovarVisible(true)}>
                  {reenvioAssinatura ? 'Reenviar para Assinatura' : 'Aprovar'}
                </Button>
                <Button type="danger" loading={actionLoading} onClick={() => setReprovarVisible(true)}>
                  Reprovar
                </Button>
              </>
            )}
          </span>
        )}
      </div>

      {podeAgir && !aguardandoAssinatura && (
        <Modal
          title={reenvioAssinatura ? 'Reenviar para Assinatura' : 'Confirmar Aprovação'}
          visible={aprovarVisible}
          onCancel={() => setAprovarVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setAprovarVisible(false)}>
              Cancelar
            </Button>,
            <Button
              key="confirm"
              type="primary"
              loading={actionLoading}
              onClick={handleAprovar}
            >
              {reenvioAssinatura ? 'Confirmar Reenvio' : 'Confirmar Aprovação'}
            </Button>,
          ]}
        >
          {assinaturaExpirada ? (
            <>
              <p>O prazo de assinatura no DocuSign expirou. Será gerado um novo envelope (prazo de 10 dias).</p>
              <p>Você será redirecionado para assinar a carta de notificação.</p>
            </>
          ) : assinaturaRecusada ? (
            <>
              <p>A assinatura foi recusada no DocuSign. Será gerado um novo envelope (prazo de 10 dias).</p>
              <p>Você será redirecionado para assinar a carta de notificação.</p>
            </>
          ) : (
            <>
              <p>Ao confirmar, você será redirecionado para o DocuSign para assinar a carta de notificação.</p>
              <p>Após a assinatura, o fornecedor será notificado automaticamente.</p>
              <p>Deseja continuar?</p>
            </>
          )}
        </Modal>
      )}

      {podeAgir && !aguardandoAssinatura && (
        <Modal
          title="Reprovar Notificação"
          visible={reprovarVisible}
          onCancel={() => { setReprovarVisible(false); setComentario(''); }}
          footer={[
            <Button key="cancel" onClick={() => { setReprovarVisible(false); setComentario(''); }}>
              Cancelar
            </Button>,
            <Button
              key="confirm"
              type="danger"
              loading={actionLoading}
              disabled={comentario.trim().length < 10}
              onClick={handleReprovar}
            >
              Confirmar Reprovação
            </Button>,
          ]}
        >
          <p>Informe o motivo da reprovação (mínimo de 10 caracteres):</p>
          <Input.TextArea
            rows={4}
            maxLength={2000}
            value={comentario}
            onChange={e => setComentario(e.target.value)}
            placeholder="Descreva o motivo da reprovação..."
          />
          <div style={{ marginTop: 8, color: comentario.trim().length < 10 ? '#ff4d4f' : '#8c8c8c', fontSize: 12 }}>
            {comentario.trim().length}/10 caracteres mínimos
          </div>
        </Modal>
      )}
    </Widget>
  );
};

const PendenciasDeloitte = () => {
  const { selectedItem } = useContext(CrudContext);
  const contratoId: string = selectedItem && selectedItem.id;

  const [documentos, setDocumentos] = useState<PendenciaDocumental[]>([]);
  const [notificacoes, setNotificacoes] = useState<NotificacaoPendenciaContrato[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [podeAprovar, setPodeAprovar] = useState(false);
  const [podeGerirExpiradaRecusada, setPodeGerirExpiradaRecusada] = useState(false);
  const [notificacaoPausada, setNotificacaoPausada] = useState(false);
  const [documentoPausa, setDocumentoPausa] = useState<string | null>(null);
  const [dataPausa, setDataPausa] = useState<string | null>(null);
  const [podeReativarNotificacao, setPodeReativarNotificacao] = useState(false);
  const [reativando, setReativando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [autoAssinarDisparado, setAutoAssinarDisparado] = useState(false);
  const PAGE_SIZE = 10;

  const carregarDados = () => {
    if (!contratoId) return;
    setLoading(true);
    Promise.all([
      getNaoConformesTodos(contratoId).then((resp: any) => setDocumentos(Array.isArray(resp) ? resp : [])),
      getPendenciasByContrato(contratoId).then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        setNotificacoes(data && Array.isArray(data.notificacoes) ? data.notificacoes : []);
        setPodeAprovar(data && data.podeAprovarNotificacoes === true);
        setPodeGerirExpiradaRecusada(data && data.podeGerirAssinaturaExpiradaOuRecusada === true);
        setNotificacaoPausada(data && data.notificacaoPausada === true);
        setDocumentoPausa(data && data.documentoPausa ? data.documentoPausa : null);
        setDataPausa(data && data.dataPausa ? data.dataPausa : null);
        setPodeReativarNotificacao(data && data.podeReativarNotificacao === true);
      }),
    ]).finally(() => setLoading(false));
  };

  const handleReativarNotificacoes = async () => {
    if (!contratoId) return;
    setReativando(true);
    try {
      const resp: any = await reativarNotificacoesPendencia(contratoId);
      if (resp && resp.valid !== false) carregarDados();
    } finally {
      setReativando(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, [contratoId]);

  // Deep-link da caixa de entrada: ?assinarNotificacao={id} — só gestor
  useEffect(() => {
    if (autoAssinarDisparado || loading || !notificacoes.length || !podeAprovar) return;
    const params = new URLSearchParams(window.location.search);
    const idAssinar = params.get('assinarNotificacao');
    if (!idAssinar) return;
    const n = notificacoes.find(
      x => x.id === idAssinar && x.statusNotificacao === 'AguardandoAssinatura',
    );
    if (!n) return;
    setAutoAssinarDisparado(true);
    getUrlAssinaturaNotificacao(n.id).then((resp: any) => {
      const data = resp && resp.data ? resp.data : resp;
      const url = (data && data.urlAssinatura) || (resp && resp.urlAssinatura);
      if (url) window.location.href = url;
    });
  }, [notificacoes, loading, podeAprovar, autoAssinarDisparado]);

  const pendentesAprovacao = notificacoes.filter(n => {
    // Gestor age; adm só visualiza (sem botões)
    if (n.statusNotificacao === 'PendenteAprovacao') return podeAprovar || podeGerirExpiradaRecusada;
    // Expirada/recusada: só adm visualiza e age
    if (n.statusNotificacao === 'AssinaturaExpirada' || n.statusNotificacao === 'AssinaturaRecusada')
      return podeGerirExpiradaRecusada;
    // Aguardando assinatura: gestor assina; adm só visualiza
    if (n.statusNotificacao === 'AguardandoAssinatura') return podeAprovar || podeGerirExpiradaRecusada;
    return false;
  });

  const podeAgirNaNotificacao = (n: NotificacaoPendenciaContrato) => {
    if (n.statusNotificacao === 'PendenteAprovacao') return podeAprovar;
    if (n.statusNotificacao === 'AssinaturaExpirada' || n.statusNotificacao === 'AssinaturaRecusada')
      return podeGerirExpiradaRecusada;
    // Assinar no DocuSign: somente gestor
    if (n.statusNotificacao === 'AguardandoAssinatura') return podeAprovar;
    return false;
  };

  const STATUS_FINAIS_SEQUENCIA = [
    'Reprovada',
    'Aprovada',
    'AguardandoAssinatura',
    'EnviadaFornecedor',
    'EnviadaDireta',
    'AssinaturaExpirada',
    'AssinaturaRecusada',
  ];

  const finaisOrdenados = [...notificacoes]
    .filter(n => STATUS_FINAIS_SEQUENCIA.indexOf(n.statusNotificacao) >= 0)
    .sort((a, b) => {
      const c = (b.competenciaMonitoramento || '').localeCompare(a.competenciaMonitoramento || '');
      if (c !== 0) return c;
      return (b.dataNotificacaoGestor || '').localeCompare(a.dataNotificacaoGestor || '');
    });

  const duasReprovacoesSequenciais =
    finaisOrdenados.length >= 2 &&
    finaisOrdenados[0].statusNotificacao === 'Reprovada' &&
    finaisOrdenados[1].statusNotificacao === 'Reprovada';

  const handleExportar = () => {
    if (!contratoId) return;
    setExportando(true);
    exportarNaoConformesContrato(contratoId).finally(() => setExportando(false));
  };

  const uniq = (field: keyof PendenciaDocumental) =>
    Array.from(new Set(documentos.map(d => String(d[field] || '')))).filter(Boolean).sort();

  const colunasTabela = [
    {
      title: 'Competência Doc.',
      dataIndex: 'competenciaDocumento',
      key: 'competenciaDocumento',
      width: 130,
      sorter: (a: PendenciaDocumental, b: PendenciaDocumental) =>
        (a.competenciaDocumento || '').localeCompare(b.competenciaDocumento || ''),
      defaultSortOrder: 'descend' as any,
      ...selectFilter(uniq('competenciaDocumento'), (v, r) => r.competenciaDocumento === v),
    },
    {
      title: 'Documento',
      dataIndex: 'nomeDocumento',
      key: 'nomeDocumento',
      width: 250,
      ...selectFilter(uniq('nomeDocumento'), (v, r) => r.nomeDocumento === v),
    },
    {
      title: 'Tipo Inconsistência',
      dataIndex: 'tipoInconsistencia',
      key: 'tipoInconsistencia',
      width: 150,
      ...selectFilter(uniq('tipoInconsistencia'), (v, r) => r.tipoInconsistencia === v),
    },
    { title: 'Inconsistência', dataIndex: 'inconsistencia', key: 'inconsistencia' },
    { title: 'Ação Corretiva', dataIndex: 'acaoCorretiva', key: 'acaoCorretiva' },
    {
      title: 'Qtd. Notificações',
      dataIndex: 'quantidadeNotificacoes',
      key: 'quantidadeNotificacoes',
      width: 110,
      render: (v: number) => v || 0,
    },
    {
      title: 'Crítico',
      dataIndex: 'critico',
      key: 'critico',
      width: 80,
      render: (v: boolean) => (v ? <span style={{ color: 'red' }}>Sim</span> : 'Não'),
    },
  ].filter(Boolean);

  if (!contratoId) return null;

  return (
    <Spin spinning={loading}>
      {pendentesAprovacao.map(n => (
        <AprovacaoPanel
          key={n.id}
          notificacao={n}
          onAcao={carregarDados}
          podeAgir={podeAgirNaNotificacao(n)}
        />
      ))}

      {duasReprovacoesSequenciais && (
        <Widget title="Regularização de pendências e notificação ao fornecedor" style={{ marginBottom: 16 }}>
          <Alert
            type="info"
            showIcon
            icon={<Icon type="close-circle" theme="filled" style={{ color: '#ff4d4f' }} />}
            style={{ background: '#fff', border: '1px solid #d9d9d9' }}
            message="Duas reprovações sequenciais de notificação"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  O gestor reprovou a notificação ao fornecedor em duas competências consecutivas
                  ({finaisOrdenados[1].competenciaMonitoramento} e {finaisOrdenados[0].competenciaMonitoramento}).
                  A regularização das pendências e a notificação ao fornecedor são necessárias para evitar
                  possíveis retenções de pagamento e bloqueios contratuais.
                </p>
                <p style={{ marginBottom: 0 }}>
                  {podeGerirExpiradaRecusada
                    ? 'Entre em contato com o gestor do contrato para esclarecimentos sobre a não notificação do fornecedor e os riscos de bloqueio/retenção.'
                    : 'O administrador do contrato foi alertado para contatá-lo e esclarecer a não notificação ao fornecedor, a fim de evitar possíveis bloqueios.'}
                </p>
              </div>
            }
          />
        </Widget>
      )}

      {notificacaoPausada && (
        <Widget title="Notificações pausadas — avaliar suspensão de pagamento" style={{ marginBottom: 16 }}>
          <Alert
            type="info"
            showIcon
            icon={<Icon type="pause-circle" theme="filled" style={{ color: '#fa8c16' }} />}
            style={{ background: '#fff', border: '1px solid #d9d9d9' }}
            message="Envio de notificações pausado por pendências críticas sequenciais"
            description={
              <div>
                <p style={{ marginBottom: 8 }}>
                  Foram identificadas <strong>2 notificações sequenciais com pendências críticas</strong>
                  {documentoPausa && documentoPausa.match(/\d{4}\/\d{2}/) ? <> desde <strong>{documentoPausa.match(/\d{4}\/\d{2}/)[0]}</strong></> : null}.
                  A partir da terceira ocorrência de pendências críticas o envio automático é <strong>pausado</strong>.
                </p>
                <p style={{ marginBottom: 8 }}>
                  Avalie a real necessidade de <strong>suspensão de pagamento</strong> conforme
                  "Manual de Gestão de Contratos de Prestação de Serviço e Locação de Bens Móveis"
                  e a interrupção das notificações. Após a análise e as providências cabíveis, reative
                  o envio se apropriado.
                </p>
                {dataPausa && (
                  <p style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 12 }}>
                    Pausado em: {new Date(dataPausa).toLocaleString('pt-BR')}
                  </p>
                )}
                {podeReativarNotificacao ? (
                  <Button type="primary" loading={reativando} onClick={handleReativarNotificacoes}>
                    Reativar notificações
                  </Button>
                ) : (
                  <p style={{ marginBottom: 0, color: '#8c8c8c' }}>
                    Aguardando o administrador do contrato reativar as notificações.
                  </p>
                )}
              </div>
            }
          />
        </Widget>
      )}

      <Widget title="Pendências Documentais (Deloitte)">
        <Table
          className="no-bold-table"
          dataSource={documentos.slice((pagina - 1) * PAGE_SIZE, pagina * PAGE_SIZE)}
          columns={colunasTabela}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: 'Nenhuma pendência não conforme encontrada.' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <Pagination
            current={pagina}
            pageSize={PAGE_SIZE}
            total={documentos.length}
            onChange={p => setPagina(p)}
            size="small"
          />
          <Button loading={exportando} onClick={handleExportar} icon="download">
            Exportar
          </Button>
        </div>
      </Widget>

      <Widget title="Histórico de Notificações">
        <Table
          className="no-bold-table"
          dataSource={notificacoes}
          rowKey="id"
          size="small"
          scroll={{ x: 900 }}
          pagination={false}
          locale={{ emptyText: 'Nenhuma notificação registrada.' }}
          columns={[
            { title: 'Competência', dataIndex: 'competenciaMonitoramento', key: 'competenciaMonitoramento', width: 120 },
            { title: 'Nº Protocolo', dataIndex: 'numeroProtocolo', key: 'numeroProtocolo', width: 150, render: (v: string) => v || '-' },
            {
              title: 'Data Envio Fornecedor', dataIndex: 'dataEnvioFornecedor', key: 'dataEnvioFornecedor', width: 160,
              render: (v: string) => v ? new Date(v).toLocaleDateString('pt-BR') : '-',
            },
            {
              title: 'Data Notif. Gestor', dataIndex: 'dataNotificacaoGestor', key: 'dataNotificacaoGestor', width: 150,
              render: (v: string) => v ? new Date(v).toLocaleDateString('pt-BR') : '-',
            },
            {
              title: 'Status', dataIndex: 'statusNotificacao', key: 'statusNotificacao', width: 170,
              render: (v: string) => {
                const s = STATUS_LABELS[v];
                const destacar = v === 'Reprovada';
                return <span style={{ color: destacar && s ? s.color : undefined }}>{s ? s.label : v}</span>;
              },
            },
          ]}
        />
      </Widget>
    </Spin>
  );
};

export default PendenciasDeloitte;
